import type {
    GemsApi_StoneClarity,
    GemsApi_StoneColour,
    GemsApi_StoneFilters,
    GemsApi_StoneSymmetry,
    GemsFindStonesQuery,
} from '@/graphql/api'
import type { ComputedRef, Ref } from 'vue'
import { computed, toValue } from 'vue'
import { useIsAdmin } from '@/components/app/isAdmin.ts'

export type DiamondSearchFilters = Pick<
    GemsApi_StoneFilters,
    | 'weight'
    | 'cut'
    | 'colour'
    | 'clarity'
    | 'polish'
    | 'symmetry'
    | 'fluorescence'
    | 'depthPercent'
    | 'tablePercent'
    | 'ratio'
    | 'priceInCurrency'
    | 'hasPhoto'
    | 'isCanadaMark'
    | 'withOriginCountry'
    | 'sorting'
    | 'length'
    | 'width'
    | 'depth'
    | 'isDtlReport'
    | 'isStock'
    | 'shippingFrom'
    | 'originCountry'
    | 'deliveryDays'
>

const forbiddenColours: GemsApi_StoneColour[] = [
    'DIAMOND_K',
    'DIAMOND_L',
    'DIAMOND_M',
]

const forbiddenClarities: GemsApi_StoneClarity[] = ['DIAMOND_SI2']

const forbiddenSymmetries: GemsApi_StoneSymmetry[] = ['GOOD', 'FAIR']

export function useNotColours(): ComputedRef<GemsApi_StoneColour[]> {
    const isAdmin = useIsAdmin()

    return computed(() => {
        if (isAdmin.value) {
            return []
        }

        return forbiddenColours
    })
}

export function useNotClarities(): ComputedRef<GemsApi_StoneClarity[]> {
    const isAdmin = useIsAdmin()

    return computed(() => {
        if (isAdmin.value) {
            return []
        }

        return forbiddenClarities
    })
}

export function useNotSymmetries(): ComputedRef<GemsApi_StoneSymmetry[]> {
    const isAdmin = useIsAdmin()

    return computed(() => {
        if (isAdmin.value) {
            return []
        }

        return forbiddenSymmetries
    })
}

export function useDiamondSearchBounds(
    bounds:
        | Ref<GemsFindStonesQuery['GemsApi_getBounds']>
        | (() => GemsFindStonesQuery['GemsApi_getBounds'])
): ComputedRef<GemsFindStonesQuery['GemsApi_getBounds']> {
    const notColours = useNotColours()
    const notClarities = useNotClarities()
    const notSymmetries = useNotSymmetries()

    return computed((): GemsFindStonesQuery['GemsApi_getBounds'] => {
        const b = toValue(bounds)

        if (!b) {
            return null
        }

        return {
            ...b,
            colour: b.colour.filter((c) => {
                return !notColours.value.includes(c)
            }),
            clarity: b.clarity.filter((c) => {
                return !notClarities.value.includes(c)
            }),
            symmetry: b.symmetry.filter((c) => {
                return !notSymmetries.value.includes(c)
            }),
        }
    })
}
